import {
  ActionResult,
  ActionType,
  AnalyticsEvent,
  BestActionResultV2Typenames,
  IntentOption,
  WizardNameV2,
} from '@varo/graphql-schema-types';
import { useFocusEffect, useRouter } from 'expo-router';
import IntentCaptureScreen from 'features/believeFunding/components/IntentCaptureScreen';
import useGetBestAction from 'features/onboarding/hooks/useGetBestAction';
import useUpdateBestAction from 'features/onboarding/hooks/useUpdateBestAction';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';
import {
  parseGQLAnalyticsEvent,
  parseOptionalGQLAnalyticsEvent,
} from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { toTagType } from 'shared/components/vxl/Tag/Tag.helpers';
import { redirectToHomePage } from 'shared/helpers/backNavigation.helpers';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';

import { IntentCaptureRadioCardProps } from './components/IntentCaptureRadioCard';

const isAnalyticsEvent = (event: unknown): event is AnalyticsEvent => {
  return (
    typeof event === 'object' &&
    event !== null &&
    'name' in event &&
    'properties' in event
  );
};

export const mapIntentOptions = (
  options: IntentOption[],
): IntentCaptureRadioCardProps[] => {
  return options?.map((option) => ({
    labelText: option?.title,
    illustration: option?.iconV2 as IllustrationKeyType,
    id: option?.id,
    action: option?.action,
    tag: option?.tagDetail?.length
      ? {
          text: option.tagDetail[0].text ?? '',
          type: toTagType(option.tagDetail[0].tagColor),
        }
      : undefined,
  }));
};

const BelieveFundingScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('onboarding', {
    keyPrefix: 'loading',
  });
  const [actionId, setActionId] = useState<string | undefined>(undefined);

  const { segmentClient } = useAnalyticsConfiguration();

  const { initiatePinwheel, isPinwheelLoading: pinWheelLoading } =
    usePinwheel();

  const {
    nextBestActionData,
    shouldCloseWizard,
    fetchNextBestAction,
    nextBestActionLoading,
    error: nextBestActionError,
  } = useGetBestAction(WizardNameV2.BelieveOnboarding, actionId);

  const {
    executeUpdateBestAction,
    updateBestActionLoading,
    error: updateBestActionError,
  } = useUpdateBestAction();

  const isLoading =
    nextBestActionLoading || updateBestActionLoading || pinWheelLoading;

  const bestActionData =
    nextBestActionData?.__typename ===
    BestActionResultV2Typenames.BestActionIntentCapture
      ? nextBestActionData
      : undefined;

  const hasError =
    nextBestActionData?.__typename ===
      BestActionResultV2Typenames.BestActionError ||
    isNotNil(nextBestActionError ?? updateBestActionError);

  const cards = mapIntentOptions(bestActionData?.options as IntentOption[]);

  const displayAnalytics =
    !isLoading && bestActionData?.analyticsEvent
      ? bestActionData.analyticsEvent
      : undefined;

  const analytics = parseOptionalGQLAnalyticsEvent(displayAnalytics);

  useViewDidFocusLogScreenAnalytics(analytics);

  const showBelieveFunding = nextBestActionData && !isLoading && !hasError;

  const handleAction = async (actionResult?: ActionResult) => {
    if (!actionResult) {
      return;
    }

    if (isAnalyticsEvent(actionResult.analyticsEvent)) {
      const analyticEvent = actionResult.analyticsEvent;
      const { name, properties } = parseGQLAnalyticsEvent(analyticEvent);
      void segmentClient.logEvent(name, properties);
    }

    try {
      const isSuccess = await executeUpdateBestAction(actionResult.id);
      if (!isSuccess) {
        return;
      }
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
    }

    setActionId(actionResult.id);

    switch (actionResult.action) {
      case ActionType.DirectDeposit:
        initiatePinwheel();
        break;
      case ActionType.Dcf:
        router.push(
          '/believe-funding/tabapay/dcf-tabapay-form?tabaPayEntryFlowType=DCF',
        );
        break;
      case ActionType.DepositCheck:
        router.push('/believe-funding/check-deposit');
        break;
      case ActionType.BankTransfer:
        router.push({
          pathname: '/believe-funding/transfer/transfer-money',
          params: { route: '/onboarding' },
        });
        break;
      case ActionType.ViewDigitalCard:
        router.push('/believe-funding/p2p/debit');
        break;
      default:
        router.push('/');
        break;
    }
  };

  useFocusEffect(
    useCallback(() => {
      if (shouldCloseWizard && !isLoading) {
        if (router.canGoBack()) {
          router.back();
        } else {
          redirectToHomePage();
        }
      }
    }, [shouldCloseWizard, isLoading, router]),
  );

  useEffect(() => {
    fetchNextBestAction();
  }, [fetchNextBestAction]);

  return (
    <View style={styles.safeArea}>
      <ScreenTransitionOverlay
        isLoading={isLoading}
        LoadingComponent={
          <FullScreenLoading
            isCoinLoader
            titleText={translatedText('titleText')}
          />
        }
        ErrorComponent={null}
      />
      {showBelieveFunding ? (
        <IntentCaptureScreen
          headlineText={bestActionData?.headline}
          cards={cards}
          handleAction={handleAction}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    ...Platform.select({ web: { maxWidth: 600 } }),
  },
});

export default BelieveFundingScreen;
